import { useEffect, useMemo, useState } from 'react';
import { FormProvider, Resolver, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui-kit/button';
import { useToast } from '@/hooks/use-toast';
import {
  AssessmentTypeKey,
  FormBuilder,
  FormDefinition,
  ReviewTypeKey,
  cloneFormDefinition,
  emptyFormDefinition,
  isAnnualPromotionForm,
  parseFormDefinition,
  serializeFormDefinition,
  validateFormDefinition,
} from '@/modules/shared/hr-form-engine';
import { FormBasicInfoFields } from '../../components/form-basic-info-fields/form-basic-info-fields';
import { useCreatePmForm, useGetPmFormById, useUpdatePmForm } from '../../hooks/use-forms';
import {
  FormBasicInfoOutput,
  FormBasicInfoValues,
  formBasicInfoSchema,
} from '../../schemas/form-basic-info-schema';
import { PmForm, PmFormsPagingResult } from '../../types/form.types';
import { FormPageSkeleton, PageHeader } from '@/components/core';
import { APP_PATHS } from '@/constant/app-paths';

const EMPTY_VALUES: FormBasicInfoValues = {
  Title: '',
  Description: '',
  AssessmentType: 'arf',
  ReviewType: '' as ReviewTypeKey,
  IsAllDepartments: false,
  DepartmentIds: [],
  IsAllDesignations: false,
  DesignationIds: [],
  ConfirmationForIntern: false,
};

const LIST_ROUTE = APP_PATHS.REVIEW_TEMPLATES;

export const FormEditorPage = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { formId } = useParams<{ formId: string }>();
  const [searchParams] = useSearchParams();

  const isDuplicate = searchParams.get('duplicate') === 'true';
  const hasSource = Boolean(formId) && formId !== 'new';
  const isEditMode = hasSource && !isDuplicate;

  const { data, isLoading } = useGetPmFormById(hasSource ? formId : undefined);
  const source: PmForm | undefined = (data as { PmForms?: PmFormsPagingResult })?.PmForms
    ?.items?.[0];

  const [definition, setDefinition] = useState<FormDefinition>(emptyFormDefinition);

  const form = useForm<FormBasicInfoValues, unknown, FormBasicInfoOutput>({
    // @hookform/resolvers v3 types the resolver with one values type; this schema's input and output differ.
    resolver: zodResolver(formBasicInfoSchema) as unknown as Resolver<
      FormBasicInfoValues,
      unknown,
      FormBasicInfoOutput
    >,
    defaultValues: EMPTY_VALUES,
  });

  useEffect(() => {
    if (!hasSource || !source) return;
    form.reset({
      Title: isDuplicate ? `${source.Title} (Copy)` : source.Title,
      Description: source.Description ?? '',
      AssessmentType: source.AssessmentType,
      ReviewType: source.ReviewType,
      Category: source.Category || undefined,
      IsAllDepartments: source.IsAllDepartments,
      DepartmentIds: source.DepartmentIds ?? [],
      IsAllDesignations: source.IsAllDesignations,
      DesignationIds: source.DesignationIds ?? [],
      ConfirmationForIntern: source.ConfirmationForIntern,
    });
    const parsed = parseFormDefinition(source);
    setDefinition(isDuplicate ? cloneFormDefinition(parsed) : parsed);
  }, [hasSource, isDuplicate, source, form]);

  const { mutateAsync: createPmForm, isPending: isCreating } = useCreatePmForm();
  const { mutateAsync: updatePmForm, isPending: isUpdating } = useUpdatePmForm();
  const isSubmitting = isCreating || isUpdating;

  const assessmentType = form.watch('AssessmentType') as AssessmentTypeKey;
  const reviewType = form.watch('ReviewType') as ReviewTypeKey;
  const isAnnualForm = useMemo(
    () => isAnnualPromotionForm(assessmentType, reviewType),
    [assessmentType, reviewType]
  );

  const handleSubmit = async (values: FormBasicInfoOutput) => {
    const errors = validateFormDefinition(definition, {
      assessmentType,
      reviewType,
      isReviewForm: true,
    });
    if (errors.length) {
      toast({ title: errors[0], variant: 'destructive' });
      return;
    }

    const stored = serializeFormDefinition(definition);
    const common = {
      Title: values.Title,
      Description: values.Description,
      IsAllDepartments: values.IsAllDepartments,
      DepartmentIds: values.DepartmentIds,
      IsAllDesignations: values.IsAllDesignations,
      DesignationIds: values.DesignationIds,
      ConfirmationForIntern: values.ConfirmationForIntern,
      ...stored,
    };

    try {
      if (isEditMode && source) {
        await updatePmForm({ itemId: source.ItemId, input: common });
      } else {
        await createPmForm({
          ...common,
          AssessmentType: values.AssessmentType as AssessmentTypeKey,
          ReviewType: values.ReviewType as ReviewTypeKey,
          Category: values.ReviewType === 'peer' ? values.Category : '',
        } as Parameters<typeof createPmForm>[0]);
      }
      navigate(LIST_ROUTE);
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  if (hasSource && isLoading) return <FormPageSkeleton fields={6} />;

  return (
    <div className="flex w-full flex-col gap-6">
      <PageHeader title={isEditMode ? t('EDIT_FORM') : t('CREATE_FORM')} />

      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-6">
          <FormBasicInfoFields isEditMode={isEditMode} />

          <div>
            <h2 className="mb-3 text-base font-semibold">{t('QUESTIONS')}</h2>
            <FormBuilder
              value={definition}
              onChange={setDefinition}
              isAnnualPromotionForm={isAnnualForm}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => navigate(LIST_ROUTE)}>
              {t('CANCEL')}
            </Button>
            <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
              {isEditMode ? t('SAVE_CHANGES') : t('CREATE')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};
